/**
 * Letterbox stills to PoYo H3 Max calibrated reference shapes so
 * `reference_image_urls` billing/token counting accepts them.
 * Calibrated: 1:1, 4:3, 3:4, 16:9, 9:16, 5:2, 2:5 (and within 2:5–5:2).
 */

export const H3_CALIBRATED_ASPECT_LABELS = [
  '1:1',
  '4:3',
  '3:4',
  '16:9',
  '9:16',
  '5:2',
  '2:5',
] as const;

export type H3CalibratedAspectLabel = (typeof H3_CALIBRATED_ASPECT_LABELS)[number];

const RATIO_BY_LABEL: Record<H3CalibratedAspectLabel, number> = {
  '1:1': 1,
  '4:3': 4 / 3,
  '3:4': 3 / 4,
  '16:9': 16 / 9,
  '9:16': 9 / 16,
  '5:2': 5 / 2,
  '2:5': 2 / 5,
};

/** Match Fragmentador / UI aspect to a calibrated label. */
export function calibratedAspectFromUi(
  aspect: '16:9' | '9:16' | '1:1' | string | null | undefined,
): H3CalibratedAspectLabel {
  if (aspect === '9:16') return '9:16';
  if (aspect === '1:1') return '1:1';
  if (aspect === '4:3') return '4:3';
  if (aspect === '3:4') return '3:4';
  return '16:9';
}

/**
 * PoYo H3 Max (full) bills `reference_image_urls` only for calibrated shapes.
 * Turbo / other models use first-last or providers that accept arbitrary refs — skip letterbox.
 */
export function modelNeedsCalibratedStills(modelId: string | null | undefined): boolean {
  return String(modelId || '') === 'poyo-h3-max';
}

export function isCalibratedPixelAspect(width: number, height: number, tol = 0.03): boolean {
  if (!width || !height) return false;
  const r = width / height;
  if (r < 2 / 5 - 0.02 || r > 5 / 2 + 0.02) return false;
  return H3_CALIBRATED_ASPECT_LABELS.some((label) => Math.abs(r - RATIO_BY_LABEL[label]) <= tol);
}

function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Falha ao carregar imagem para normalizar aspect.'));
    img.src = src;
  });
}

async function blobFromSource(source: Blob | string): Promise<Blob> {
  if (typeof source !== 'string') return source;
  if (source.startsWith('data:') || source.startsWith('blob:')) {
    const res = await fetch(source);
    return res.blob();
  }
  if (/^https?:\/\//i.test(source)) {
    const res = await fetch(source);
    if (!res.ok) throw new Error(`Falha ao baixar still (${res.status}).`);
    return res.blob();
  }
  throw new Error('Fonte de imagem inválida para normalização.');
}

function drawLetterbox(
  img: CanvasImageSource,
  canvasW: number,
  canvasH: number,
  drawW: number,
  drawH: number,
  offsetX: number,
  offsetY: number,
  name: string,
  quality: number,
  fill: string,
): Promise<File> {
  const canvas = document.createElement('canvas');
  canvas.width = canvasW;
  canvas.height = canvasH;
  const ctx = canvas.getContext('2d');
  if (!ctx) return Promise.reject(new Error('Canvas indisponível.'));
  ctx.fillStyle = fill;
  ctx.fillRect(0, 0, canvasW, canvasH);
  ctx.drawImage(img, offsetX, offsetY, drawW, drawH);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (!b) {
          reject(new Error('Falha ao exportar JPEG normalizado.'));
          return;
        }
        resolve(new File([b], name, { type: 'image/jpeg' }));
      },
      'image/jpeg',
      quality,
    );
  });
}

/**
 * Letterbox (or pillarbox) onto a calibrated aspect canvas.
 * Pads with near-black so bars stay discreet as video refs.
 */
export async function letterboxStillToAspect(
  source: Blob | string,
  targetLabel: H3CalibratedAspectLabel,
  opts?: { maxEdge?: number; quality?: number; fill?: string },
): Promise<{ file: File; width: number; height: number; changed: boolean }> {
  const maxEdge = opts?.maxEdge ?? 2048;
  const quality = opts?.quality ?? 0.92;
  const fill = opts?.fill ?? '#0a0a0a';
  const targetRatio = RATIO_BY_LABEL[targetLabel];
  const name = `still-${targetLabel.replace(':', 'x')}-${Date.now()}.jpg`;

  const blob = await blobFromSource(source);
  const objectUrl = URL.createObjectURL(blob);
  try {
    const img = await loadImageElement(objectUrl);
    const srcW = img.naturalWidth || img.width;
    const srcH = img.naturalHeight || img.height;
    if (!srcW || !srcH) throw new Error('Imagem sem dimensões.');

    const srcRatio = srcW / srcH;
    const alreadyOk =
      Math.abs(srcRatio - targetRatio) <= 0.03 && isCalibratedPixelAspect(srcW, srcH);

    if (alreadyOk && Math.max(srcW, srcH) <= maxEdge) {
      if (blob.type.includes('jpeg') || blob.type.includes('jpg')) {
        return {
          file: new File([blob], name, { type: 'image/jpeg' }),
          width: srcW,
          height: srcH,
          changed: false,
        };
      }
      const jpeg = await drawLetterbox(img, srcW, srcH, srcW, srcH, 0, 0, name, quality, fill);
      return { file: jpeg, width: srcW, height: srcH, changed: true };
    }

    let canvasW: number;
    let canvasH: number;
    if (srcRatio > targetRatio) {
      canvasW = srcW;
      canvasH = Math.round(srcW / targetRatio);
    } else {
      canvasH = srcH;
      canvasW = Math.round(srcH * targetRatio);
    }

    const scale = Math.min(1, maxEdge / Math.max(canvasW, canvasH));
    canvasW = Math.max(1, Math.round(canvasW * scale));
    canvasH = Math.max(1, Math.round(canvasH * scale));
    const drawW = Math.round(srcW * scale);
    const drawH = Math.round(srcH * scale);
    const offsetX = Math.round((canvasW - drawW) / 2);
    const offsetY = Math.round((canvasH - drawH) / 2);

    const file = await drawLetterbox(
      img,
      canvasW,
      canvasH,
      drawW,
      drawH,
      offsetX,
      offsetY,
      name,
      quality,
      fill,
    );
    return { file, width: canvasW, height: canvasH, changed: true };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
